import { Dialog } from 'primereact/dialog';
import { CloseIcon, CopyIcon, Toast } from '@repo/atomic-components';
import { OutlineButton } from '@/ui/components/OutlineButton';
import { GreenButton } from '@/ui/components/GreenButton';
import { isEndpointLockError } from '@/api';
import { GdprSummaryResponse, useCompleteReviewMutation } from '@/api/gdpr';
import { FC, ReactNode, useRef, useState } from 'react';
import { useGdprControlsState } from '../GdprControlsContext';
import { GDPR_REVIEW_COMPLETED_KEY } from '../constants';
import { copyToClipboard } from '@repo/utils';

interface GdprSummaryModalProps {
  visible: boolean;
  onHide: () => void;
  summaryData: GdprSummaryResponse | null;
  isLoading?: boolean;
  /**
   * @deprecated kept for backwards-compat with the parent prop wiring; the modal now resolves
   * page navigation itself via GdprControlsContext.onGoToOriginalPageNumber so that the value
   * sent in (Page.pageNumber, which is an originalPageNumber) is mapped to the correct page
   * index in the working set rather than treated as a 1-based working-set index.
   */
  onPageClick?: (pageNumber: number) => void;
  documentId?: string;
  /**
   * @deprecated no longer used; navigation after review was replaced with window.close().
   */
  caseId?: string;
}

/**
 * DialogShell
 * Shared chrome for all three states (loading, no-data, summary). Mirrors the
 * MoveSectionsDialog design on the bundle screen — fixed 500px max width,
 * 8px corner radius, fletchers-gray-110 1px border, custom header (PrimeReact's
 * default header is hidden), 24px / 16px content padding.
 */
const DialogShell: FC<{
  visible: boolean;
  onHide: () => void;
  title: string;
  body: ReactNode;
  footer?: ReactNode;
}> = ({ visible, onHide, title, body, footer }) => (
  <Dialog
    visible={visible}
    onHide={onHide}
    style={{
      width: '90vw',
      maxWidth: '500px',
      borderRadius: '8px',
      border: '1px solid #e1e1e0',
      overflow: 'hidden',
    }}
    contentClassName="!p-0"
    headerClassName="!hidden"
  >
    <div className="border-fletchers-gray-110 flex items-center justify-between border-b px-[24px] py-[16px]">
      <div className="text-fletchers-black-900 text-[18px]/[18px] font-[600]">{title}</div>
      <button
        type="button"
        aria-label="Close"
        onClick={onHide}
        className="cursor-pointer rounded p-1 focus:outline-none"
      >
        <CloseIcon width={12} height={12} />
      </button>
    </div>

    <div className="flex flex-col gap-[16px] px-[24px] py-[16px]">{body}</div>

    {footer && <div className="flex items-center gap-2 px-[24px] pb-[24px]">{footer}</div>}
  </Dialog>
);

export const GdprSummaryModal = ({
  visible,
  onHide,
  summaryData,
  isLoading = false,
  documentId,
}: GdprSummaryModalProps) => {
  const [completeReview, { isLoading: isCompletingReview }] = useCompleteReviewMutation();
  const toast = useRef<Toast>(null);
  const [isCompleted, setIsCompleted] = useState(false);
  // Page.pageNumber from the summary response is the source-document originalPageNumber,
  // not a 1-based working-set index. Use the controls-context handler that resolves
  // originalPageNumber → working-set index before scrolling.
  const { onGoToOriginalPageNumber } = useGdprControlsState();

  const handleClose = () => {
    onHide();
  };

  const handleConfirm = async () => {
    if (!documentId) {
      localStorage.setItem(GDPR_REVIEW_COMPLETED_KEY, Date.now().toString());
      window.close();
      setIsCompleted(true);
      return;
    }

    try {
      await completeReview({ documentId }).unwrap();
      localStorage.setItem(GDPR_REVIEW_COMPLETED_KEY, Date.now().toString());
      window.close();
      setIsCompleted(true);
    } catch (error) {
      console.error('Failed to complete review:', error);
      // The lock's own toast has already explained this one
      if (isEndpointLockError(error)) return;
      toast.current?.show({
        severity: 'error',
        summary: 'Confirmation Failed',
        detail: 'Failed to complete GDPR review. Please try again.',
        life: 5000,
      });
    }
  };

  const handlePageClick = (originalPageNumber: number) => {
    onGoToOriginalPageNumber(originalPageNumber);
    onHide();
  };

  const handleCopy = async () => {
    if (!summaryData) return;
    const breachPageNumbers =
      summaryData.breachPages && summaryData.breachPages.length > 0
        ? summaryData.breachPages.map((p) => p.pageNumber).join(', ')
        : 'None';
    // Plain-text version of every line shown in the modal body, in display order.
    const text = [
      `Total pages in document: ${summaryData.pageCount}`,
      `AI-supported check: ${summaryData.aiChecked ? 'True' : 'False'}`,
      `Total pages flagged by AI: ${summaryData.flagCount}`,
      `Total pages with Breach label: ${summaryData.breachCount}`,
      `Page numbers of breaches: ${breachPageNumbers}`,
      `Total pages with Blank label: ${summaryData.blankCount}`,
    ].join('\n');
    await copyToClipboard(text);
    toast.current?.show({
      severity: 'success',
      summary: 'Copied',
      detail: 'Summary copied to clipboard.',
      life: 2000,
    });
  };

  if (isCompleted) {
    return (
      <>
        <Toast ref={toast} />
        <DialogShell
          visible={visible}
          onHide={handleClose}
          title="GDPR check summary"
          body={
            <div className="text-fletchers-gray-175 text-[14px]">
              GDPR check completed successfully. This window may now be closed.
            </div>
          }
        />
      </>
    );
  }

  if (isLoading) {
    return (
      <>
        <Toast ref={toast} />
        <DialogShell
          visible={visible}
          onHide={handleClose}
          title="GDPR check summary"
          body={<div className="text-fletchers-gray-175 text-[14px]">Loading summary…</div>}
        />
      </>
    );
  }

  if (!summaryData) {
    return (
      <>
        <Toast ref={toast} />
        <DialogShell
          visible={visible}
          onHide={handleClose}
          title="GDPR check summary"
          body={<div className="text-fletchers-gray-175 text-[14px]">No summary data available.</div>}
        />
      </>
    );
  }

  const { breachPages, breachCount, blankCount, flagCount, pageCount, aiChecked } = summaryData;

  return (
    <>
      <Toast ref={toast} />
      <DialogShell
        visible={visible}
        onHide={handleClose}
        title="GDPR check summary"
        body={
          <>
            <div className="text-[14px] text-black">Total pages in document: {pageCount}</div>

            <div className="text-[14px] text-black">
              <span>AI-supported check: </span>
              <span className="text-fletchers-green-500 font-semibold">{aiChecked ? 'True' : 'False'}</span>
            </div>

            <div className="text-[14px] text-black">Total pages flagged by AI: {flagCount}</div>
            <div className="text-[14px] text-black">Total pages with Breach label: {breachCount}</div>

            <div className="text-[14px] text-black">
              <span>Page numbers of breaches: </span>
              {breachPages && breachPages.length > 0 ? (
                breachPages.map((page, index) => (
                  <span key={page.pageId}>
                    <button
                      type="button"
                      onClick={() => handlePageClick(page.pageNumber)}
                      className="text-fletchers-green-500 hover:text-fletchers-green-800 cursor-pointer font-semibold"
                    >
                      {page.pageNumber}
                    </button>
                    {index < breachPages.length - 1 && ', '}
                  </span>
                ))
              ) : (
                <span className="text-fletchers-green-500 font-semibold">None</span>
              )}
            </div>

            <div className="text-[14px] text-black">Total pages with Blank label: {blankCount}</div>
          </>
        }
        footer={
          <>
            {/* Copy — bottom-left, white outlined button. Copies every line in the modal
                body as plain text, joined with newlines. Same CopyIcon used on the
                fletch chat message copy button. */}
            <OutlineButton className="!h-[32px]" icon={<CopyIcon width={14} height={14} />} onClick={handleCopy}>
              <span className="ml-2">Copy</span>
            </OutlineButton>
            <div className="ml-auto flex gap-2">
              <OutlineButton className="!h-[32px]" onClick={handleClose}>
                Cancel
              </OutlineButton>
              <GreenButton className="!h-[32px]" onClick={handleConfirm} disabled={isCompletingReview}>
                Confirm
              </GreenButton>
            </div>
          </>
        }
      />
    </>
  );
};
